import * as React from "react";
import { IInputFieldProps, ArrayFieldSchema, isObjectFieldSchema } from "@generation/core";
import { Button, ButtonLayout } from "@shared/common";

import { useFormContext, useFieldArray, FieldValues } from "react-hook-form";
//eslint-disable-next-line import/no-cycle
import { RenderFields } from "../Form/RenderFields";
import { useErrorMessage } from "../Validations/useErrorMessage";
import { useDependsOnHook } from "../Hoc/useDependsOnHook";
import { ArrayItemsRequiredValidation } from "../Validations/ArrayItemsRequiredValidation";
import { mapValidationsToRecord } from "../Validations/ValidationHelper";
import "./ArrayField.scss";
import { useFieldRenderContext } from "../Context/FieldRenderContextProvider";
import { renderAddedSchemaFields } from "../Interfaces/renderAddedSchemaFields";
import { withFormFieldHoc } from "./hoc/withFormFieldHoc";

export const ArrayField = (renderAddedSchemaFields?: renderAddedSchemaFields) =>
  React.memo(
    withFormFieldHoc((props: IInputFieldProps<ArrayFieldSchema>) => {
      const { control, unregister, formState, getValues } = useFormContext();
      const renderContext = useFieldRenderContext();

      if (!renderContext) {
        return <></>;
      }

      let validations = props.field.validate ?? [];

      const dependsOnResult = useDependsOnHook(props.name, props.field.dependsOn);

      if (dependsOnResult.isRequired !== undefined) {
        if (dependsOnResult.isRequired === true) {
          validations = [
            ...validations,
            ...(!validations.find((item) => item instanceof ArrayItemsRequiredValidation)
              ? [new ArrayItemsRequiredValidation(true)]
              : [])
          ];
        } else {
          validations = [
            ...validations.filter(
              (item) =>
                !(item instanceof ArrayItemsRequiredValidation) ||
                (item instanceof ArrayItemsRequiredValidation && item.dependsOn === false)
            )
          ];
        }
      }

      const { fields, append, remove, update } = useFieldArray<FieldValues, string, "id">({
        name: props.name,
        control,
        rules: {
          required: validations.some((item) => item instanceof ArrayItemsRequiredValidation),
          validate: {
            ...mapValidationsToRecord(
              props.name.substring(0, props.name.lastIndexOf(".")) ?? "",
              validations.filter((item) => !(item instanceof ArrayItemsRequiredValidation))
            )
          }
        }
      });

      const error = useErrorMessage(`${props.name}.root`, props.field, validations ?? null);
      const showAddButton =
        !props.field.maximumNumberOfRows ||
        (props.field.maximumNumberOfRows && fields.length < props.field.maximumNumberOfRows);

      const buttonLayout = props.field.buttonLayout ? props.field.buttonLayout : ButtonLayout.Transparent;
      const showCloseButton = props.field.withClosing === undefined || props.field.withClosing;
      return (
        <React.Fragment>
          {error && formState.isSubmitted && (
            <span className="input__error alert error-text" role="alert">
              {error}
            </span>
          )}
          {fields.map((item, index) => (
            <renderContext.array key={item.id ?? index} showCloseButton={showCloseButton} field={props.field}>
              {showCloseButton && (
                <button
                  className="close-icon"
                  type="button"
                  aria-label={`${props.name} verwijderen`}
                  title={`${props.name} verwijderen`}
                  id={`${props.name}-${index}-verwijderen`}
                  key={`${props.name}-${index}-verwijderen`}
                  onClick={() => {
                    if (isObjectFieldSchema(props.field.items)) {
                      try {
                        Object.entries(props.field.items.properties).forEach(([fieldName, _field]) => {
                          unregister(`${props.name}[${index}].${fieldName}`, {
                            keepDefaultValue: false
                          });
                        });
                      } catch (error) {
                        //
                      }
                    }
                    remove(index);
                  }}
                >
                  {" "}
                </button>
              )}
              <RenderFields
                field={props.field.items}
                name={`${props.name}[${index}]`}
                renderAddedSchemaFields={renderAddedSchemaFields}
              />
              {props.field.buttonsBelowItem && (
                <div className={`action-buttons ${props.field.alignmentButtonsBelowItem ?? "right"}`}>
                  {props.field.buttonsBelowItem.map((item) => (
                    <Button
                      id={item.id}
                      key={item.id}
                      buttonText={item.text}
                      type={item.type != "link" ? item.type : "button"}
                      clickHandler={(event) =>
                        item.handle(event, {
                          values: getValues(`${props.name}[${index}]`),
                          setUpdate: (values: Record<string, any>) => {
                            update(index, values);
                          }
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </renderContext.array>
          ))}

          {showAddButton && props.field.hideAddButton !== true && (
            <Button
              id={`${props.name}-toevoegen`}
              key={`${props.name}-toevoegen`}
              clickHandler={() => {
                let defaultObject = {};
                if (isObjectFieldSchema(props.field.items)) {
                  Object.entries(props.field).forEach(([fieldName, _field], index) => {
                    defaultObject[`${fieldName}`] = "";
                  });
                }
                append(defaultObject);
              }}
              buttonText={`${props.field.addLabel} ${
                props.field.hideToevoegenInAddLabel === true ? "" : "toevoegen"
              }`.trim()}
              layout={buttonLayout}
            />
          )}
        </React.Fragment>
      );
    })
  );
